import { v4 as uuidv4 } from 'uuid';

import { isCanonicalUtc, toCanonicalUtc } from '../utils/availability-slot';
import { isTourAvailabilityTreatment, TOUR_AVAILABILITY_VARIATION_KEYS } from './tour-availability-experiment';

const STORAGE_PREFIX = 'self-show:cdep-8540:scheduling-journey:v1:';
const EXPIRY_MS = 30 * 60 * 1000;

function storageKey(unitId) {
  return `${STORAGE_PREFIX}${String(unitId)}`;
}

function isCurrentAssignment(assignment, unitId) {
  return Boolean(
    assignment &&
      assignment.unitId === String(unitId) &&
      TOUR_AVAILABILITY_VARIATION_KEYS.includes(assignment.variationKey) &&
      Date.parse(assignment.expiresAt) > Date.now(),
  );
}

function read(unitId) {
  try {
    const assignment = JSON.parse(window.localStorage.getItem(storageKey(unitId)));
    if (isCurrentAssignment(assignment, unitId)) {
      return assignment;
    }
  } catch {
    // Invalid or unavailable storage falls back to a fresh Control evaluation.
  }

  removeAssignment(unitId);
  return null;
}

function write(unitId, assignment) {
  try {
    window.localStorage.setItem(storageKey(unitId), JSON.stringify(assignment));
    return true;
  } catch {
    return false;
  }
}

export function readAssignment(unitId) {
  const assignment = read(unitId);
  return { valid: Boolean(assignment), assignment };
}

export function saveAssignment({ unitId, journeyId = uuidv4(), variationKey }) {
  if (!TOUR_AVAILABILITY_VARIATION_KEYS.includes(variationKey)) {
    return { valid: false, assignment: null };
  }

  const createdAt = toCanonicalUtc(Date.now());
  const assignment = {
    version: 1,
    journeyId,
    unitId: String(unitId),
    variationKey,
    createdAt,
    expiresAt: toCanonicalUtc(Date.now() + EXPIRY_MS),
    selectedStartUtc: null,
  };

  return write(unitId, assignment) ? { valid: true, assignment } : { valid: false, assignment: null };
}

export function readSelectedSlot(unitId) {
  const assignment = read(unitId);
  const valid =
    assignment && isTourAvailabilityTreatment(assignment.variationKey) && isCanonicalUtc(assignment.selectedStartUtc);

  return valid ? { valid: true, startUtc: assignment.selectedStartUtc } : { valid: false, startUtc: null };
}

export function saveSelectedSlot(unitId, startUtc) {
  const assignment = read(unitId);
  if (!assignment || !isTourAvailabilityTreatment(assignment.variationKey) || !isCanonicalUtc(startUtc)) {
    return { valid: false, startUtc: null };
  }

  const next = {
    ...assignment,
    selectedStartUtc: startUtc,
    expiresAt: toCanonicalUtc(Date.now() + EXPIRY_MS),
  };

  return write(unitId, next) ? { valid: true, startUtc } : { valid: false, startUtc: null };
}

export function removeSelectedSlot(unitId) {
  const assignment = read(unitId);
  if (assignment) {
    write(unitId, { ...assignment, selectedStartUtc: null });
  }
}

export function removeAssignment(unitId) {
  try {
    window.localStorage.removeItem(storageKey(unitId));
  } catch {
    // Control is the fallback when storage is unavailable.
  }
}
